import Marquee from "react-fast-marquee";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "./motion";
import { api, fmtPrice } from "../../lib/api";

export function AreaMarquee({ areas = [] }) {
  const items = areas.length ? areas : ["Manhattan", "The Hamptons", "Aspen", "Malibu", "Palm Beach"];
  return (
    <section data-testid="marquee" className="bg-[#0A1128] py-8 overflow-hidden">
      <Marquee speed={38} gradient={false}>
        {[...items, ...items].map((a, i) => (
          <span key={i} className="font-serif italic text-3xl md:text-5xl text-[#FAF9F6] mx-8 flex items-center gap-8">
            {a} <span className="text-[var(--gold)] not-italic">✦</span>
          </span>
        ))}
      </Marquee>
    </section>
  );
}

export function Services({ services = [] }) {
  return (
    <section id="services" data-testid="services-section" className="bg-[#FAF9F6] py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mb-16">
          <Reveal><Eyebrow>Services</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-[#0A1128] mt-5">
              Representation, <span className="italic text-[var(--gold)]">refined.</span>
            </h2>
          </Reveal>
        </div>
        <div className="border-t border-[#0A1128]/10">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="group grid md:grid-cols-12 gap-6 items-center py-10 border-b border-[#0A1128]/10 hover:bg-[#E8E4D9]/30 transition-colors duration-500 px-2">
                <div className="md:col-span-1 font-serif text-2xl text-[var(--gold)]/60">0{i + 1}</div>
                <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl text-[#0A1128]">{s.title}</h3>
                <p className="md:col-span-7 text-[var(--slate)] leading-relaxed">{s.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SoldShowcase() {
  const [sold, setSold] = useState([]);
  useEffect(() => { api.get("/properties", { params: { status: "Sold" } }).then((r) => setSold(r.data)); }, []);
  if (!sold.length) return null;
  return (
    <section id="sold" data-testid="sold-section" className="bg-[#FAF9F6] py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><Eyebrow>Proven Results</Eyebrow></Reveal>
            <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-6xl tracking-tight text-[#0A1128] mt-5">Recently Sold</h2></Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="text-[var(--slate)] max-w-sm text-[15px] leading-relaxed">A selection of homes successfully closed for my clients across the Cayman Islands.</p>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {sold.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.1}>
              <Link to={`/property/${p.id}`} data-testid={`sold-card-${p.id}`} className="group block">
                <div className="hover-img relative">
                  <img src={p.images?.[0]} alt={p.title} className="w-full h-72 object-cover grayscale-[35%] group-hover:grayscale-0 transition-all duration-700" />
                  <span className="absolute inset-0 grid place-items-center">
                    <span className="bg-[#0A1128] text-[#FAF9F6] font-serif italic text-3xl px-8 py-3 rotate-[-4deg]">Sold</span>
                  </span>
                </div>
                <div className="flex items-start justify-between mt-5 gap-4">
                  <div>
                    <h3 className="font-serif text-2xl text-[#0A1128]">{p.title}</h3>
                    <p className="text-[var(--slate)] text-sm mt-1">{p.location}</p>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)]">Sold For</div>
                    <div className="font-serif text-xl text-[#0A1128]">{fmtPrice(p.sold_price || p.price, p.currency)}</div>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials({ testimonials = [] }) {
  const [showAll, setShowAll] = useState(false);
  if (!testimonials.length) return null;
  const visible = showAll ? testimonials : testimonials.slice(0, 3);
  const hasMore = testimonials.length > 3;
  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-[#0A1128] text-[#FAF9F6] py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <Reveal><Eyebrow>Client Voices</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-5">In their words.</h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="text-[#FAF9F6]/60 text-sm max-w-xs">A selection of {testimonials.length} reviews from buyers, sellers and clients across the Cayman Islands.</p>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
          {visible.map((t, i) => (
            <Reveal key={t.id || i} delay={(i % 3) * 0.1}>
              <div className="border-t border-[#FAF9F6]/20 pt-8 h-full flex flex-col">
                <p className="font-serif text-xl leading-relaxed italic text-[#FAF9F6]/90 flex-1">“{t.quote}”</p>
                <div className="flex items-center gap-4 mt-8">
                  {t.photo
                    ? <img src={t.photo} alt={t.name} className="w-11 h-11 object-cover rounded-full" />
                    : <span className="grid place-items-center w-11 h-11 rounded-full bg-[var(--gold)]/20 text-[var(--gold)] font-serif text-lg">{t.name?.[0]}</span>}
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-[#FAF9F6]/50 uppercase tracking-[0.14em]">{t.role}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        {hasMore && (
          <div className="mt-16 text-center">
            <button data-testid="testimonials-toggle" onClick={() => setShowAll(!showAll)}
              className="px-9 py-4 border border-[#FAF9F6]/30 text-sm uppercase tracking-[0.14em] text-[#FAF9F6] hover:bg-[#FAF9F6] hover:text-[#0A1128] transition-colors duration-300">
              {showAll ? "Show Less" : `Read All ${testimonials.length} Reviews`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

